import { Component, OnInit, HostListener, AfterViewInit } from "@angular/core";
import { HttpClient } from "@angular/common/http";

// Import the shared calculation service
declare var require: any;
import { DisabilityCalculator } from "../Services/disability-calculator";

// Import bootstrap-datepicker
import * as $ from "jquery";
import "bootstrap-datepicker";
import { MONTH_NAMES } from "../constants/constants";

@Component({
  selector: "app-disablity-calculator",
  templateUrl: "./disablity-calculator.component.html",
  styleUrls: ["./skeleton-loader.component.css"],
})
export class DisablityCalculatorComponent implements OnInit {
  // Screen size detection properties
  isIpadPortrait = false;

  // Form validation properties
  formErrors: any = {
    employmentInfo: {},
    performanceBenefits: {},
    financialInfo: {},
  };

  isFormValid = false; // Start as invalid - all fields required
  isCalculating = false;
  showValidationErrors = false; // Only show validation errors after calculate button is clicked

  // Loading states
  isLoadingFormData = true; // Show skeleton/loading state for form fields
  apiLoadingError = false; // Track if API call failed

  // Page configuration from CMS
  pageTitle = ""; // Will be loaded from CMS via API
  disclaimer = ""; // Will be loaded from CMS via API
  notes = ""; // Will be loaded from CMS via API
  isDisclaimerExpanded = false; // Track disclaimer expansion state

  // Form data properties - start empty, populate from API
  employmentInfo = {
    cafo: "",
    dateOfBirth: "",
    hireDate: "",
    dateOfRetirement: "",
    dateOfDisability: "",
  };

  performanceBenefits = {
    longevity: 0, // Auto-calculated based on hire date and disability date
    rate: "", // Auto-calculated based on YOS and seat
    tfps: 0, // Auto-calculated based on rate and disability date (read-only)
    averageTfp: 0, // Default TFP from API, user can modify within constraints
    mbe: 0, // Auto-calculated: TFP rate × average TFP (read-only, currency formatted)
    sickBankStart: 0,
    sickUseAfterEp: "1.0", // User input with default 1.0, min 0.0, max Avg TFPs x 50%
  };

  financialInfo = {
    federalTaxBracket: "",
    stateTaxBracket: "",
    preTax: 0,
    roth: 0,
    weeksOfVacation: 0,
    stdPlan: "",
    ltdPlan: "",
    lolTaxable: "",
  };

  // Results data
  activeTab = "monthly-income";
  yearRange = "";
  availableYearRanges: string[] = [];

  // Store the calculated LTD and STD plans for display in results
  calculatedLtdPlan = "";
  calculatedStdPlan = "";

  // Data for different tables - will be populated by calculations
  monthlyIncomeData: any[] = [];
  monthlyRetirementData: any[] = [];

  annualIncomeData: any[] = [];
  annualRetirementData: any[] = [];
  private calculator: DisabilityCalculator;

  constructor(private http: HttpClient) {
    // Set current date as default disability date
    this.employmentInfo.dateOfDisability = this.getCurrentDateFormatted();

    // Initialize form with default values
    this.initializeDefaults();
    // Calculation service instance
    this.calculator = new DisabilityCalculator(http);
  }

  ngOnInit() {
    // Don't show validation errors initially - only validate silently
    this.validateForm();

    // Start loading form data from API
    this.isLoadingFormData = true;
    this.apiLoadingError = false;

    // Load data from API
    this.calculator.getDisabilityCalcDetails().subscribe({
      next: (data: any) => {
        // Clear stale form data just before populating with fresh API data
        this.clearFormData();
        
        // Use calculator's method to handle its own data (best practice) - MUST be first
        this.calculator.setApiData(data);

        // Populate form fields from API response
        this.populateFormFromAPI(data);

        // Calculate initial rate based on current disability date and API data
        if (
          this.employmentInfo.dateOfDisability &&
          this.employmentInfo.hireDate
        ) {
          this.updateCalculatedLongevity();
          this.updateRate();
          this.updateTfps();
          this.updateCalculatedWeekOfVactions();
        }

        // Re-validate after populating
        this.validateForm();

        // Hide loading state
        this.isLoadingFormData = false;


        // Initialize datepicker values after data is loaded
        this.initializeDatepickerValues();
      },
      error: (error: any) => {
        this.apiLoadingError = true;
        this.isLoadingFormData = false;

        // No default values - if API fails, form remains empty
        this.validateForm();


        // Initialize datepicker values
        this.initializeDatepickerValues();
      },
    });
  }


  /**
   * Initialize datepicker values - using imported bootstrap-datepicker
   */
  private initializeDatepickerValues() {
    setTimeout(() => {
      // Use the imported jQuery with bootstrap-datepicker
      const datepickerElements = $("input[datepicker]");

      datepickerElements.each(function () {
        const $this = $(this);
        const currentValue = $this.val() as string;

        try {
          // Check if this element already has datepicker data
          if ($this.data("datepicker")) {
            if (currentValue && typeof currentValue === "string") {
              const dateObj = new Date(currentValue);
              $this.datepicker("setDate", dateObj);
              $this.datepicker("update", dateObj);
            }
          } else {
            // Initialize datepicker
            $this.datepicker({
              autoclose: true,
              orientation: "bottom auto",
              format: "mm/dd/yyyy",
            });

            // Set the date if value exists
            if (currentValue && typeof currentValue === "string") {
              const dateObj = new Date(currentValue);
              $this.datepicker("setDate", dateObj);
            }
          }
        } catch (e) {
          // Silent error handling
        }
      });
    }, 500);
  }

  private initializeDefaults() {
    // Keep form empty to show validation errors initially
    // Data will be populated via API simulation

    // Initialize screen size detection
    this.checkScreenSize();
  }

  /**
   * Get current date formatted as MM/DD/YYYY
   */
  private getCurrentDateFormatted(): string {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const year = today.getFullYear();
    return `${month}/${day}/${year}`;
  }



  /**
   * Retry API call when it fails
   */
  retryApiCall() {
    this.ngOnInit(); // Simply re-run the initialization
  }

  @HostListener("window:resize")
  onResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    const width = window.innerWidth;
    const height = window.innerHeight;

    // iPad portrait detection: width between 768-1366px and height > width
    // iPad Air: 820x1180, iPad Pro: 1024x1366, iPad Mini: 768x1024
    this.isIpadPortrait = width >= 768 && width <= 1366 && height > width;
  }

  // Basic validation with required fields and placeholder conditions
  validateForm() {
    this.validateEmploymentInfo();
    this.validatePerformanceBenefits();
    this.validateFinancialInfo();

    this.isFormValid =
      this.isEmploymentInfoValid() &&
      this.isPerformanceBenefitsValid() &&
      this.isFinancialInfoValid();
  }

  validateEmploymentInfo() {
    // Initialize if not exists
    if (!this.formErrors.employmentInfo) {
      this.formErrors.employmentInfo = {};
    }

    // All Employment Info fields are REQUIRED - only add errors for empty fields
    if (this.isEmpty(this.employmentInfo.cafo)) {
      this.formErrors.employmentInfo.cafo = "Seat selection is required";
    } else {
      delete this.formErrors.employmentInfo.cafo;
    }

    if (this.isEmpty(this.employmentInfo.dateOfBirth)) {
      this.formErrors.employmentInfo.dateOfBirth = "Date of Birth is required";
    } else {
      delete this.formErrors.employmentInfo.dateOfBirth;
    }

    if (this.isEmpty(this.employmentInfo.hireDate)) {
      this.formErrors.employmentInfo.hireDate = "Hire Date is required";
    } else {
      delete this.formErrors.employmentInfo.hireDate;
    }

    if (this.isEmpty(this.employmentInfo.dateOfRetirement)) {
      this.formErrors.employmentInfo.dateOfRetirement =
        "Date of Retirement is required";
    } else {
      delete this.formErrors.employmentInfo.dateOfRetirement;
    }
    if (this.isEmpty(this.employmentInfo.dateOfDisability)) {
      this.formErrors.employmentInfo.dateOfDisability =
        "Date of Disability is required";
    } else {
      delete this.formErrors.employmentInfo.dateOfDisability;
    }

    // Date validation - assign errors to specific fields
    this.validateDateCombinations();
  }

  validatePerformanceBenefits() {
    const errors: any = {};

    // All Performance & Benefits fields are REQUIRED
    if (this.isEmpty(this.performanceBenefits.longevity)) {
      errors.longevity = "Longevity is required";
    } else {
      const longevity = parseFloat(this.performanceBenefits.longevity.toString());
      if (longevity < 0) {
        errors.longevity = "Longevity cannot be negative";
      } else if (longevity > 48) {
        errors.longevity = "Longevity cannot exceed 48 years";
      }
    }

    if (this.isEmpty(this.performanceBenefits.rate)) {
      errors.rate = "Rate is required";
    }

    // TFPs is now auto-calculated and read-only, so no validation needed
    // if (this.isEmpty(this.performanceBenefits.tfps)) {
    //   errors.tfps = 'TFPs is required';
    // }

    if (this.isEmpty(this.performanceBenefits.averageTfp)) {
      errors.averageTfp = "Average TFP is required";
    } else {
      const avgTfp = parseFloat(this.performanceBenefits.averageTfp.toString());
      if (!isNaN(avgTfp)) {
        if (avgTfp < 0) {
          errors.averageTfp = "Average TFP cannot be negative";
        } else if (avgTfp > 500) {
          errors.averageTfp = "Average TFP cannot exceed 500";
        } else {
          // Validate Average TFP against TfpConstraintRanges from API if available
          const minValue = this.getTfpConstraintMin();
          const maxValue = this.getTfpConstraintMax();

          if (minValue !== null && avgTfp < minValue) {
            errors.averageTfp = `Average TFP must be at least ${minValue}`;
          } else if (maxValue !== null && avgTfp > maxValue) {
            errors.averageTfp = `Average TFP cannot exceed ${maxValue}`;
          }
        }
      }
    }

    if (this.isEmpty(this.performanceBenefits.sickBankStart)) {
      errors.sickBankStart = "Sick Bank Start is required";
    } else {
      const sickBank = parseFloat(this.performanceBenefits.sickBankStart.toString());
      if (!isNaN(sickBank)) {
        if (sickBank < 0) {
          errors.sickBankStart = "Sick Bank Start cannot be negative";
        } else if (sickBank > 3000) {
          errors.sickBankStart = "Sick Bank Start cannot exceed 3000";
        }
      }
    }

     // Sick TFP To Be Used With LOL validation
    if (this.isEmpty(this.performanceBenefits.sickUseAfterEp)) {
      errors.sickUseAfterEp = 'Sick TFP To Be Used With LOL is required';
    } else {
      const sickUse = parseFloat(this.performanceBenefits.sickUseAfterEp.toString());
      if (isNaN(sickUse)) {
        errors.sickUseAfterEp = 'Sick TFP To Be Used With LOL must be a valid number';
      } else if (sickUse < 0.0) {
        errors.sickUseAfterEp = 'Sick TFP To Be Used With LOL cannot be negative';
      } else {
        // Validate against 50% of average TFP
        const avgTfp = parseFloat(this.performanceBenefits.averageTfp.toString());
        if (!isNaN(avgTfp) && avgTfp > 0) {
          const maxAllowed = avgTfp * 0.5;
          if (sickUse > maxAllowed) {
            errors.sickUseAfterEp = `Sick TFP cannot exceed 50% of Average TFP (${maxAllowed.toFixed(1)})`;
          }
        }
      }
    }

    
    // MBE validation - check for required TFP Rate and Average TFP
    if (this.performanceBenefits.tfps <= 0 || this.isEmpty(this.performanceBenefits.averageTfp)) {
      errors.mbe = 'TFP Rate and Average TFP are required';
    }
    
    this.formErrors.performanceBenefits = errors;
  }

  validateFinancialInfo() {
    const errors: any = {};

    // All Financial Information fields are REQUIRED
    if (this.isEmpty(this.financialInfo.federalTaxBracket)) {
      errors.federalTaxBracket = "Federal Tax Bracket is required";
    } else {
      const federalTax = parseFloat(this.financialInfo.federalTaxBracket.toString());
      if (!isNaN(federalTax)) {
        // Validate Federal Tax Bracket against FederalTaxConstraintRanges from API if available
        const minValue = this.getFederalTaxConstraintMin();
        const maxValue = this.getFederalTaxConstraintMax();
        
        if (minValue !== null && federalTax < minValue) {
          errors.federalTaxBracket = `Federal Tax Bracket cannot be less than ${minValue}%`;
        } else if (maxValue !== null && federalTax > maxValue) {
          errors.federalTaxBracket = `Federal Tax Bracket cannot exceed ${maxValue}%`;
        }
      }
    }

    if (this.isEmpty(this.financialInfo.stateTaxBracket)) {
      errors.stateTaxBracket = "State Tax Bracket is required";
    } else {
      const stateTax = parseFloat(this.financialInfo.stateTaxBracket.toString());
      if (!isNaN(stateTax)) {
        if (stateTax < 0) {
          errors.stateTaxBracket = "State Tax Bracket cannot be negative";
        } else if (stateTax > 100) {
          errors.stateTaxBracket = "State Tax Bracket cannot exceed 100%";
        }
      }
    }

    if (this.isEmpty(this.financialInfo.preTax)) {
      errors.preTax = "Pre-Tax 401K Contribution is required";
    } else {
      const preTax = Number(this.financialInfo.preTax);
      if (!isNaN(preTax)) {
        if (preTax < 0) {
          errors.preTax = "Pre-Tax 401K Contribution cannot be negative";
        } else if (preTax > 100) {
          errors.preTax = "Pre-Tax 401K Contribution cannot exceed 100%";
        }
      }
    }

    if (this.isEmpty(this.financialInfo.roth)) {
      errors.roth = "Roth 401K Contribution is required";
    } else {
      const roth = Number(this.financialInfo.roth);
      if (!isNaN(roth)) {
        if (roth < 0) {
          errors.roth = "Roth 401K Contribution cannot be negative";
        } else if (roth > 100) {
          errors.roth = "Roth 401K Contribution cannot exceed 100%";
        }
      }
    }

    // Combined Pre-Tax and Roth validation - cannot exceed 80% total
    const preTaxValue = Number(this.financialInfo.preTax) || 0;
    const rothValue = Number(this.financialInfo.roth) || 0;
    const combinedTotal = preTaxValue + rothValue;
    
    if (combinedTotal > 80) {
      errors.preTax = `Combined Pre-Tax and Roth cannot exceed 80%`;
      errors.roth = `Combined Pre-Tax and Roth cannot exceed 80%`;
    }

    if (this.isEmpty(this.financialInfo.weeksOfVacation)) {
      errors.weeksOfVacation = "Weeks of vacation is required";
    } else {
      const weeksVacation = parseFloat(this.financialInfo.weeksOfVacation.toString());
      if (!isNaN(weeksVacation) && weeksVacation < 0) {
        errors.weeksOfVacation = "Weeks of vacation cannot be negative";
      }
    }

    if (this.isEmpty(this.financialInfo.stdPlan)) {
      errors.stdPlan = "STD Plan selection is required";
    }

    if (this.isEmpty(this.financialInfo.ltdPlan)) {
      errors.ltdPlan = "LTD Plan selection is required";
    }

    if (this.isEmpty(this.financialInfo.lolTaxable)) {
      errors.lolTaxable = "LOL taxable selection is required";
    }

    this.formErrors.financialInfo = errors;
  }

  private isEmploymentInfoValid(): boolean {
    return Object.keys(this.formErrors.employmentInfo).length === 0;
  }

  private isPerformanceBenefitsValid(): boolean {
    return Object.keys(this.formErrors.performanceBenefits).length === 0;
  }

  private isFinancialInfoValid(): boolean {
    return Object.keys(this.formErrors.financialInfo).length === 0;
  }

  // Calculation methods
  onCalculate() {
    // First validate the form
    this.validateForm();

    // Show validation errors if form is invalid
    if (!this.isFormValid) {
      this.showValidationErrors = true;

      return;
    }

    // Hide validation errors if form is valid
    this.showValidationErrors = false;

    this.isCalculating = true;

    // Simulate calling external JS calculation file
    setTimeout(() => {
      this.performCalculations();
      this.isCalculating = false;
    }, 1000);
  }

  private performCalculations() {
    try {
      // Generate year ranges FIRST to ensure yearRange is set before calculations
      this.generateYearRanges();
  
      const formData = this.getAllFormData();

      // Use the shared calculation service
      // The unified table generation will update sickUseAfterEp automatically
      const calculationResults = this.calculator.calculate(formData);

      // Update component data with calculated table data
      this.monthlyIncomeData = calculationResults.monthlyIncomeTableData;
      this.monthlyRetirementData =
        calculationResults.monthlyRetirementTableData;
      this.annualIncomeData = calculationResults.annualIncomeTableData;
      this.annualRetirementData = calculationResults.annualRetirementTableData;

  

      // Update the calculated LTD and STD plans for display in results
      this.calculatedLtdPlan = this.financialInfo.ltdPlan;
      this.calculatedStdPlan = this.financialInfo.stdPlan;

    } catch (error) {
      // Handle calculation errors gracefully
      alert("Calculation failed: " + error.message);
    }
  }

  private getAllFormData() {
    return {
      employmentInfo: this.employmentInfo,
      performanceBenefits: {
        ...this.performanceBenefits,
        // Convert string sickUseAfterEp back to number for calculations
        sickUseAfterEp: parseFloat(this.performanceBenefits.sickUseAfterEp.toString()) || 0
      },
      financialInfo: {
        ...this.financialInfo,
        // Convert numeric preTax and roth to string with % for calculations
        preTax: this.financialInfo.preTax !== undefined && this.financialInfo.preTax !== null ? this.financialInfo.preTax.toString() + "%" : "",
        roth: this.financialInfo.roth !== undefined && this.financialInfo.roth !== null ? this.financialInfo.roth.toString() + "%" : "",
        // Ensure federalTaxBracket is properly formatted for calculations
        federalTaxBracket: this.financialInfo.federalTaxBracket || ""
      },
      yearRange: this.yearRange,
      activeTab: this.activeTab,
    };
  }

  private datePickerMonitoring: any = {};

  // Start monitoring a datepicker input for value changes
  public startDatePickerMonitoring(section: string, field: string, event: any) {
    const inputElement = event.target;
    const monitoringKey = `${section}.${field}`;

    // Clear any existing monitoring for this field
    if (this.datePickerMonitoring[monitoringKey]) {
      clearInterval(this.datePickerMonitoring[monitoringKey]);
    }

    // Store initial value from the model, not the input element
    let originalValue = "";
    if (section === "employmentInfo") {
      originalValue = (this.employmentInfo as any)[field] || "";
    }

    // Store the last value we've seen
    let lastValue = inputElement.value;

    // Start monitoring for value changes
    this.datePickerMonitoring[monitoringKey] = setInterval(() => {
      const currentValue = inputElement.value;

      // If value changed
      if (currentValue !== lastValue) {
        // If the new value is not empty, update the model
        if (currentValue.trim() !== "") {
          // Update the model
          if (section === "employmentInfo") {
            (this.employmentInfo as any)[field] = currentValue;
          }

          // Clear validation error
          if (this.formErrors[section] && this.formErrors[section][field]) {
            delete this.formErrors[section][field];
          }

          // Update calculated longevity if hire date or disability date changed
          if (
            section === "employmentInfo" &&
            (field === "hireDate" || field === "dateOfDisability")
          ) {
            this.updateCalculatedLongevity();
            // Also update rate when dates change
            this.updateRate();
            // Also update the week of vacations
            this.updateCalculatedWeekOfVactions();
          }

          // Update TFPs if disability date changed
          if (section === "employmentInfo" && field === "dateOfDisability") {
            this.updateTfps();
          }

          // Update retirement date if date of birth changed (reverse date logic)
          if (section === "employmentInfo" && field === "dateOfBirth") {
            this.updateRetirementDateFromBirthDate();
          }


          // Stop monitoring after successful update
          clearInterval(this.datePickerMonitoring[monitoringKey]);
          delete this.datePickerMonitoring[monitoringKey];
        } else if (currentValue.trim() === "" && originalValue) {
          // If the value became empty (user cleared or canceled), restore original value
          inputElement.value = originalValue;
          if (section === "employmentInfo") {
            (this.employmentInfo as any)[field] = originalValue;
          }
        }

        // Update last value
        lastValue = currentValue;
      }
    }, 100);

    // Stop monitoring after 10 seconds to prevent memory leaks
    setTimeout(() => {
      if (this.datePickerMonitoring[monitoringKey]) {
        // Restore original value if field is empty when monitoring stops
        if (inputElement.value.trim() === "" && originalValue) {
          inputElement.value = originalValue;
          if (section === "employmentInfo") {
            (this.employmentInfo as any)[field] = originalValue;
          }
        }
        clearInterval(this.datePickerMonitoring[monitoringKey]);
        delete this.datePickerMonitoring[monitoringKey];
      }
    }, 10000);
  }

  // Form field change handler - fires immediately as user types
  onFieldChange(section: string, field: string) {
    // Always clear specific field error if it has a value (regardless of showValidationErrors)
    const value = this.getFieldValue(section, field);

    if (this.isValidValue(value)) {
      if (this.formErrors[section] && this.formErrors[section][field]) {
        delete this.formErrors[section][field];
      }
    }

    // Update calculated longevity if hire date or disability date changed
    if (
      section === "employmentInfo" &&
      (field === "hireDate" || field === "dateOfDisability")
    ) {
      this.updateCalculatedLongevity();
      // Also update rate when dates change
      this.updateRate();
      // Also update week of vacations 
      this.updateCalculatedWeekOfVactions();
    }

    // Update TFPs if disability date changed
    if (section === "employmentInfo" && field === "dateOfDisability") {
      this.updateTfps();
    }

    // Update retirement date if date of birth changed (reverse date logic)
    if (section === "employmentInfo" && field === "dateOfBirth") {
      this.updateRetirementDateFromBirthDate();
    }


    // Update rate dropdown options if seat selection changed
    if (section === "employmentInfo" && field === "cafo") {
      // Clear current rate selection when seat changes
      this.performanceBenefits.rate = "";
      // The dropdown will repopulate automatically via getAvailableRateOptions()
      // Then auto-select the appropriate rate based on YOS
      this.updateRate();
    }

    // Update TFPs if rate is manually changed
    if (section === "performanceBenefits" && field === "rate") {
      this.updateTfps();
    }

    // Update MBE if average TFP is manually changed
    if (section === "performanceBenefits" && field === "averageTfp") {
      this.updateMBE();
    }

    // Update disability date if longevity is manually changed
    if (section === "performanceBenefits" && field === "longevity") {
      this.updateDisabilityDateFromLongevity();
    }

    // Re-validate the form
    this.validateForm();
  }

  /**
   * Handle paste events for date fields to ensure longevity and validation updates
   */
  onDatePaste(section: string, field: string, event: any) {
    setTimeout(() => {
      const element = event.target;
      const pastedValue = element.value;

      // Update the model with the pasted value
      (this as any)[section][field] = pastedValue;

      // Trigger the same logic as onFieldChange
      this.onFieldChange(section, field);

      // Update datepicker calendar if it's a valid date
      if (pastedValue && this.isValidDateFormat(pastedValue)) {
        this.updateDatepicker(field, pastedValue);
      }
    }, 10);
  }

  /**
   * Handle input events for date fields to ensure longevity and validation updates
   */
  onDateInput(section: string, field: string, event: any) {
    setTimeout(() => {
      const element = event.target;
      const inputValue = element.value;

      // Update the model with the input value
      (this as any)[section][field] = inputValue;

      // Trigger the same logic as onFieldChange
      this.onFieldChange(section, field);

      // Update datepicker calendar if it's a valid date
      if (inputValue && this.isValidDateFormat(inputValue)) {
        this.updateDatepicker(field, inputValue);
      }
    }, 10);
  }

  /**
   * Check if a date string is in valid MM/DD/YYYY format
   */
  private isValidDateFormat(dateString: string): boolean {
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!dateRegex.test(dateString)) {
      return false;
    }

    // Try to create a valid date object
    const parts = dateString.split("/");
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  }

  /**
   * Implement reverse date logic for retirement date calculation
   */
  updateRetirementDateFromBirthDate() {
    if (this.employmentInfo.dateOfBirth) {
      const birthDate = new Date(this.employmentInfo.dateOfBirth);
      
      if (!isNaN(birthDate.getTime())) {
        // Calculate retirement date (same month/day as birth date, 65 years later)
        const retirementYear = birthDate.getFullYear() + 65;
        const month = String(birthDate.getMonth() + 1).padStart(2, "0");
        const day = String(birthDate.getDate()).padStart(2, "0");
        
        this.employmentInfo.dateOfRetirement = `${month}/${day}/${retirementYear}`;
        
        // Update the datepicker to reflect the new date
        this.updateDatepicker(
          "dateOfRetirement",
          this.employmentInfo.dateOfRetirement
        );
      }
    }
  }

  /**
   * Calculate birth date from retirement date (reverse logic)
   */
  updateBirthDateFromRetirementDate() {
    if (this.employmentInfo.dateOfRetirement) {
      const retirementDate = new Date(this.employmentInfo.dateOfRetirement);
      
      if (!isNaN(retirementDate.getTime())) {
        // Calculate birth date (same month/day as retirement date, 65 years earlier)
        const birthYear = retirementDate.getFullYear() - 65;
        const month = String(retirementDate.getMonth() + 1).padStart(2, "0");
        const day = String(retirementDate.getDate()).padStart(2, "0");
        
        this.employmentInfo.dateOfBirth = `${month}/${day}/${birthYear}`;
        
        // Update the datepicker to reflect the new date
        this.updateDatepicker(
          "dateOfBirth",
          this.employmentInfo.dateOfBirth
        );
      }
    }
  }

  /**
   * Calculate disability date from manually entered longevity
   * Uses reverse calculation: disabilityYear = hireYear + longevity - 1
   */
  updateDisabilityDateFromLongevity() {
    if (this.employmentInfo.hireDate && this.performanceBenefits.longevity) {
      const hireDate = new Date(this.employmentInfo.hireDate);
      const longevity = parseFloat(this.performanceBenefits.longevity.toString());
      
      if (!isNaN(hireDate.getTime()) && !isNaN(longevity) && longevity > 0) {
        // Calculate disability date based on longevity
        // longevity = disabilityYear - hireYear + 1
        // therefore: disabilityYear = hireYear + longevity - 1
        const disabilityYear = hireDate.getFullYear() + longevity - 1;
        
        // Keep the same month and day as hire date for consistency
        const month = String(hireDate.getMonth() + 1).padStart(2, "0");
        const day = String(hireDate.getDate()).padStart(2, "0");
        
        this.employmentInfo.dateOfDisability = `${month}/${day}/${disabilityYear}`;
        
        // Update the datepicker to reflect the new date
        this.updateDatepicker(
          "dateOfDisability",
          this.employmentInfo.dateOfDisability
        );

        // Update rate and TFPs based on new disability date
        this.updateRate();
        this.updateTfps();
        this.updateCalculatedWeekOfVactions();
      }
    }
  }

  /**
   * Validate date combinations for invalid scenarios
   */
  private validateDateCombinations(): void {
    const { dateOfBirth, hireDate, dateOfRetirement, dateOfDisability } = this.employmentInfo;

    // Skip validation if any date is empty
    if (!dateOfBirth || !hireDate || !dateOfRetirement || !dateOfDisability) {
      return;
    }

    const dob = new Date(dateOfBirth);
    const hire = new Date(hireDate);
    const retirement = new Date(dateOfRetirement);
    const disability = new Date(dateOfDisability);

    // Skip validation if any date is invalid
    if (isNaN(dob.getTime()) || isNaN(hire.getTime()) || isNaN(retirement.getTime()) || isNaN(disability.getTime())) {
      return;
    }

    // Check invalid combinations and assign errors to specific fields
    if (hire < dob) {
      this.formErrors.employmentInfo.hireDate = "Hire Date cannot be earlier than Date of Birth";
    }

    if (hire.getTime() === dob.getTime()) {
      this.formErrors.employmentInfo.hireDate = "Hire Date cannot be the same as Date of Birth";
    }

    // Check if pilot is at least 18 years old at hire date
    const minimumAgeDate = new Date(dob.getTime());
    minimumAgeDate.setFullYear(dob.getFullYear() + 18);
    if (hire < minimumAgeDate) {
      this.formErrors.employmentInfo.hireDate = "Pilot must be at least 18 years old at hire date";
    }

    if (retirement < hire) {
      this.formErrors.employmentInfo.dateOfRetirement = "Retirement Date cannot be earlier than Hire Date";
    }

    if (disability < dob) {
      this.formErrors.employmentInfo.dateOfDisability = "Disability Date cannot be earlier than Date of Birth";
    }

    if (disability < hire) {
      this.formErrors.employmentInfo.dateOfDisability = "Disability Date cannot be earlier than Hire Date";
    }

    if (disability.getTime() === dob.getTime()) {
      this.formErrors.employmentInfo.dateOfDisability = "Disability Date cannot be the same as Date of Birth";
    }

    if (disability.getTime() === hire.getTime()) {
      this.formErrors.employmentInfo.dateOfDisability = "Disability Date cannot be the same as Hire Date";
    }

    // Check if disability date is after retirement date
    if (disability > retirement) {
      this.formErrors.employmentInfo.dateOfDisability = "Disability Date cannot be greater than Retirement Date";
    }

    // Check if disability date is before 2025
    if (disability.getFullYear() < 2025) {
      this.formErrors.employmentInfo.dateOfDisability = "Disability Date cannot be before year 2025";
    }
  }

  /**
   * Calculate and update longevity based on hire date and disability date
   */
  updateCalculatedLongevity() {
    if (this.employmentInfo.hireDate && this.employmentInfo.dateOfDisability) {
      const calculatedValue = this.calculator.calculateLongevity(
        this.employmentInfo.hireDate,
        this.employmentInfo.dateOfDisability
      );

      // Prevent negative longevity - fallback to 0, and round to whole number
      this.performanceBenefits.longevity = Math.max(0, Math.round(calculatedValue));
    } else {
      this.performanceBenefits.longevity = 0;
    }
  }

  /**
   * Calculate and update Weeks Of Vacation based on Years Of Servics
   */
  updateCalculatedWeekOfVactions() {
    if (!isNaN(this.performanceBenefits.longevity)) {
      const calculatedValue = this.calculator.calculateWeeksOfVacation(
       this.performanceBenefits.longevity
      );

      // Update the existing longevity field that's bound to the UI
      this.financialInfo.weeksOfVacation = calculatedValue;
    } else {
      this.financialInfo.weeksOfVacation = 0;
    }
  }



  /**
   * Calculate and update retirement date based on date of birth (age 65)
   */
  updateRetirementDate() {
    if (this.employmentInfo.dateOfBirth) {
      const birthDate = new Date(this.employmentInfo.dateOfBirth);

      if (!isNaN(birthDate.getTime())) {
        // Calculate retirement date (65 years after birth)
        const retirementDate = new Date(birthDate);
        retirementDate.setFullYear(birthDate.getFullYear() + 65);

        // Format the date as MM/DD/YYYY
        const month = String(retirementDate.getMonth() + 1).padStart(2, "0");
        const day = String(retirementDate.getDate()).padStart(2, "0");
        const year = retirementDate.getFullYear();

        this.employmentInfo.dateOfRetirement = `${month}/${day}/${year}`;

        // Update the datepicker to reflect the new date
        this.updateDatepicker(
          "dateOfRetirement",
          this.employmentInfo.dateOfRetirement
        );
      }
    }
  }

  /**
   * Update a specific datepicker with a new date value
   */
  private updateDatepicker(fieldName: string, dateValue: string) {
    setTimeout(() => {
      const element = $(`input[name="${fieldName}"]`);
      if (element.length > 0 && element.data("datepicker")) {
        try {
          const dateObj = new Date(dateValue);
          element.datepicker("setDate", dateObj);
          element.datepicker("update", dateObj);
        } catch (e) {
          // Silent error handling
        }
      }
    }, 50);
  }

  /**
   * Calculate and update Rate based on YOS and Seat selection
   * Examples: CA12, FO6, FO12, etc.
   */
  updateRate() {
    if (
      !this.employmentInfo.cafo ||
      !this.employmentInfo.hireDate ||
      !this.employmentInfo.dateOfDisability
    ) {
      return;
    }

    // Calculate YOS (Years of Service)
    const hireDate = new Date(this.employmentInfo.hireDate);
    const disabilityDate = new Date(this.employmentInfo.dateOfDisability);

    if (isNaN(hireDate.getTime()) || isNaN(disabilityDate.getTime())) {
      return;
    }

    const yos = disabilityDate.getFullYear() - hireDate.getFullYear() + 1;

    // Get available rate levels from API data (TFP rates)
    const availableRateLevels = this.getAvailableRateLevels();

    if (availableRateLevels.length === 0) {
      // Fallback to common levels if API data not available
      availableRateLevels.push(1, 3, 6, 12);
    }

    // Find the closest rate level that is less than or equal to YOS
    let closestLevel = 1; // Default to minimum
    for (const level of availableRateLevels) {
      if (level <= yos) {
        closestLevel = level;
      } else {
        break;
      }
    }

    // Construct the rate code
    const rateCode = `${this.employmentInfo.cafo}${closestLevel}`;
    this.performanceBenefits.rate = rateCode;

    // Also update TFPs when rate changes
    this.updateTfps();
  }

  /**
   * Get available rate levels from API TFP rates data
   * Extracts the numeric part from rate codes like CA12, FO6, etc.
   */
  private getAvailableRateLevels(): number[] {
    const levels = new Set<number>();

    // Check if we have TFP rates from API
    if (this.calculator) {
      if (
        this.calculator.tfpRatesFromAPI &&
        this.calculator.tfpRatesFromAPI.length > 0
      ) {
        const tfpRates = this.calculator.tfpRatesFromAPI;

        for (const rate of tfpRates) {
          if (rate.YOS) {
            // Extract number from rate codes like "CA12", "FO6", etc.
            const match = rate.YOS.match(/\d+/);
            if (match) {
              const level = parseInt(match[0], 10);
              levels.add(level);
            }
          }
        }
      }
    }

    const result = Array.from(levels).sort((a, b) => a - b);

    // Convert Set to sorted array
    return result;
  }

  /**
   * Get available rate options for dropdown based on selected seat (CA or FO)
   * @returns Array of rate options like CA1, CA3, CA6, CA12 or FO1, FO3, FO6, FO12
   */
  getAvailableRateOptions(): string[] {
    if (!this.employmentInfo.cafo) {
      return [];
    }

    const rateLevels = this.getAvailableRateLevels();
    if (rateLevels.length === 0) {
      return [];
    }

    // Create rate options based on seat selection
    return rateLevels.map((level) => `${this.employmentInfo.cafo}${level}`);
  }

  /**
   * Calculate and update TFPs value based on calculated rate and disability date
   * Uses the existing getTFPRate method to look up values from API TFP rate tables
   */
  updateTfps() {
    if (
      !this.performanceBenefits.rate ||
      !this.employmentInfo.dateOfDisability
    ) {
      return;
    }

    // Extract year from disability date
    const disabilityDate = new Date(this.employmentInfo.dateOfDisability);
    if (isNaN(disabilityDate.getTime())) {
      return;
    }

    const year = disabilityDate.getFullYear();

    // Use calculator's getTFPRate method to get the TFP value
    if (this.calculator) {
      const tfpValue = this.calculator.getTFPRate(
        this.performanceBenefits.rate,
        year
      );

      // Update the TFPs field
      this.performanceBenefits.tfps = tfpValue;
      
      // Update MBE calculation
      this.updateMBE();
    }
  }

  /**
   * Calculate and update MBE (TFP rate × average TFP)
   */
  updateMBE() {
    if (this.performanceBenefits.tfps > 0 && this.performanceBenefits.averageTfp > 0) {
      this.performanceBenefits.mbe = this.performanceBenefits.tfps * this.performanceBenefits.averageTfp;
    } else {
      this.performanceBenefits.mbe = 0;
    }
  }

  /**
   * Get formatted MBE value as currency string
   */
  getFormattedMBE(): string {
    if (this.performanceBenefits.mbe > 0) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(this.performanceBenefits.mbe);
    }
    return '';
  }

  /**
   * Get minimum value for Average TFP from TfpConstraintRanges API data
   */
  private getTfpConstraintMin(): number {
    if (
      this.calculator &&
      this.calculator.apiData &&
      this.calculator.apiData.TfpConstraintRanges
    ) {
      return this.calculator.apiData.TfpConstraintRanges.MinValue;
    }
    return null; // No validation if API data not available
  }

  /**
   * Get maximum value for Average TFP from TfpConstraintRanges API data
   */
  private getTfpConstraintMax(): number {
    if (
      this.calculator &&
      this.calculator.apiData &&
      this.calculator.apiData.TfpConstraintRanges
    ) {
      return this.calculator.apiData.TfpConstraintRanges.MaxValue;
    }
    return null; // No validation if API data not available
  }

  /**
   * Get minimum value for Federal Tax Bracket from FederalTaxConstraintRanges API data
   */
  private getFederalTaxConstraintMin(): number {
    if (
      this.calculator &&
      this.calculator.apiData &&
      this.calculator.apiData.FederalTaxConstraintRanges
    ) {
      return this.calculator.apiData.FederalTaxConstraintRanges.MinValue;
    }
    return null; // No validation if API data not available
  }

  /**
   * Get maximum value for Federal Tax Bracket from FederalTaxConstraintRanges API data
   */
  private getFederalTaxConstraintMax(): number {
    if (
      this.calculator &&
      this.calculator.apiData &&
      this.calculator.apiData.FederalTaxConstraintRanges
    ) {
      return this.calculator.apiData.FederalTaxConstraintRanges.MaxValue;
    }
    return null; // No validation if API data not available
  }

  // Helper method to check if a value is valid (not empty)
  private isValidValue(value: any): boolean {
    return !this.isEmpty(value);
  }

  // Comprehensive helper method to check if a value is empty
  private isEmpty(value: any): boolean {
    // Handle null, undefined
    if (value === null || value === undefined) return true;

    // Handle strings
    if (typeof value === "string") return value.trim() === "";

    // Handle numbers - empty if NaN, but 0 is valid
    if (typeof value === "number") return isNaN(value);

    // Handle arrays
    if (Array.isArray(value)) return value.length === 0;

    // Handle objects
    if (typeof value === "object") return Object.keys(value).length === 0;

    // Everything else is considered not empty
    return false;
  }

  // Helper method to get field value
  private getFieldValue(section: string, field: string): any {
    if (section === "employmentInfo") {
      return (this.employmentInfo as any)[field];
    } else if (section === "performanceBenefits") {
      return (this.performanceBenefits as any)[field];
    } else if (section === "financialInfo") {
      return (this.financialInfo as any)[field];
    }
    return null;
  }

  // Utility methods for validation feedback
  hasError(section: string, field: string): boolean {
    // Only show errors after Calculate button is pressed AND there are validation errors
    const hasErrorResult =
      this.showValidationErrors &&
      this.formErrors[section] &&
      this.formErrors[section][field];
    return !!hasErrorResult;
  }

  getError(section: string, field: string): string {
    return (this.formErrors[section] && this.formErrors[section][field]) || "";
  }

  onTabChange(tabName: string) {
    this.activeTab = tabName;
    // Update results based on selected tab
  }

  /**
   * Toggle disclaimer expansion state
   */
  toggleDisclaimer() {
    this.isDisclaimerExpanded = !this.isDisclaimerExpanded;
  }

  /**
   * Handle year range dropdown change
   * Recalculate data when user selects a different year range
   */
  onYearRangeChange() {
    // Only recalculate if we already have results (calculation was run before) AND form is valid
    if (this.monthlyIncomeData.length > 0) {
      // Validate form first
      this.validateForm();
      
      // Only proceed if form is valid
      if (this.isFormValid) {
        this.isCalculating = true;
        
        // Add small delay to show the loader, then recalculate
        setTimeout(() => {
          this.performCalculations();
          this.isCalculating = false;
        }, 500);
      } else {
        // If form has errors, show validation errors
        this.showValidationErrors = true;
      }
    }
  }

  /**
   * Calculate retirement year - use actual retirement date if available
   */
  private calculateRetirementYear(): number {
    // Use the actual retirement date if available
    if (this.employmentInfo.dateOfRetirement) {
      const retirementDate = new Date(this.employmentInfo.dateOfRetirement);
      if (!isNaN(retirementDate.getTime())) {
        return retirementDate.getFullYear();
      }
    }
  }

  /**
   * Populate form fields from API response
   */
  private populateFormFromAPI(apiData: any): void {
    // Map API fields to form fields

    // Page configuration from CMS
    this.pageTitle = apiData.PageTitle || "Disability Calculator";
    this.disclaimer = apiData.Disclaimer || "";
    this.notes = apiData.Notes || "";

    // Employment Info
    if (apiData.DOB) {
      this.employmentInfo.dateOfBirth = apiData.DOB;
    }
    if (apiData.HireDate) {
      this.employmentInfo.hireDate = apiData.HireDate;
    }

    // Seat/CAFO - Find selected value from Seat array
    if (apiData.Seat && apiData.Seat.length > 0) {
      const selectedSeat =
        apiData.Seat.find((s: any) => s.Selected) || apiData.Seat[0];
      this.employmentInfo.cafo = selectedSeat.Value;
    }

    // Performance Benefits
    if (apiData.SickBankStart !== undefined) {
      this.performanceBenefits.sickBankStart = apiData.SickBankStart;
    }
    if (apiData.DefaultTfp !== undefined) {
      this.performanceBenefits.averageTfp = apiData.DefaultTfp;
    }

    // Financial Info
    if (apiData.PreTax !== undefined) {
      this.financialInfo.preTax = apiData.PreTax;
    }
    if (apiData.Roth !== undefined) {
      this.financialInfo.roth = apiData.Roth;
    }

    // Federal Tax Bracket - Get from CMS
    if (apiData.FederalTaxBracket !== undefined) {
      this.financialInfo.federalTaxBracket = apiData.FederalTaxBracket.toString();
    }

    // State Tax Bracket - Get first available or selected
    if (apiData.StateTaxBrackets !== undefined) {
      this.financialInfo.stateTaxBracket = apiData.StateTaxBrackets.toString();
    }

    // STD Plan - Get selected or default
    if (apiData.StdPlan && apiData.StdPlan.length > 0) {
      const selectedStd =
        apiData.StdPlan.find((s: any) => s.Selected) || apiData.StdPlan[0];
      this.financialInfo.stdPlan = selectedStd.Value;
    }

    // LTD Plan - Get selected or default
    if (apiData.LtdPlan && apiData.LtdPlan.length > 0) {
      const selectedLtd =
        apiData.LtdPlan.find((s: any) => s.Selected) || apiData.LtdPlan[0];
      this.financialInfo.ltdPlan = selectedLtd.Value;
    }

    // LOL Taxable - Get selected or default
    if (apiData.LoLTaxable && apiData.LoLTaxable.length > 0) {
      const selectedLol =
        apiData.LoLTaxable.find((s: any) => s.Selected) ||
        apiData.LoLTaxable[0];
      this.financialInfo.lolTaxable = selectedLol.Value.toLowerCase();
    }

    // Store dropdown options and constraints for later use
    this.dropdownOptions = {
      seat: apiData.Seat,
      stateTaxBrackets: apiData.StateTaxBrackets,
      stdPlan: apiData.StdPlan,
      ltdPlan: apiData.LtdPlan,
      lolTaxable: apiData.LoLTaxable,
      tfpConstraintRanges: apiData.TfpConstraintRanges,
    };

    // Calculate longevity after populating form data
    this.updateCalculatedLongevity();

    // Calculate retirement date based on date of birth from API
    this.updateRetirementDate();

    // Calculate rate based on seat and YOS
    this.updateRate();
    this.updateTfps();

    // Calculate week of vacations after populating form data
    this.updateCalculatedWeekOfVactions();
  }

  // Store dropdown options
  dropdownOptions: any = {};

  /**
   * Generate available year ranges from disability year to retirement year
   */
  private generateYearRanges(): void {
    // Use disability year as start year if available, otherwise use current year
    let startYear = new Date().getFullYear();
    if (this.employmentInfo.dateOfDisability) {
      const disabilityDate = new Date(this.employmentInfo.dateOfDisability);
      if (!isNaN(disabilityDate.getTime())) {
        startYear = disabilityDate.getFullYear();
      }
    }
    
    const retirementYear = this.calculateRetirementYear();

    // Preserve the currently selected year range
    const currentlySelectedRange = this.yearRange;

    this.availableYearRanges = [];

    // Generate year ranges (each range covers 2 years)
    for (let year = startYear; year <= retirementYear; year += 2) {
      const endYear = Math.min(year + 1, retirementYear);
      if (year === endYear) {
        // Single year case - show as 2-year format for consistency (e.g., "2025 - 2026")
        this.availableYearRanges.push(`${year} - ${year + 1}`);
      } else {
        // Normal 2-year range
        this.availableYearRanges.push(`${year} - ${endYear}`);
      }
    }

    // Only set default year range if no range is currently selected or if the selected range is no longer valid
    if (
      currentlySelectedRange &&
      this.availableYearRanges.includes(currentlySelectedRange)
    ) {
      // Keep the currently selected range
      this.yearRange = currentlySelectedRange;
    } else if (this.availableYearRanges.length > 0) {
      // Set default year range to the first one only if no valid selection exists
      this.yearRange = this.availableYearRanges[0];
    }
  }


  /**
   * Prevent invalid characters (including 'e') in number inputs
   * Allows only digits, decimal point, and control keys
   */
  preventAllInvalidChars(event: KeyboardEvent): void {
    const key = event.key;
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;

    // Allow control keys (Backspace, Delete, Tab, Enter, Escape, Arrow keys, Home, End)
    const controlKeys = [
      'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    if (controlKeys.includes(key)) {
      return;
    }

    // Allow Ctrl/Cmd combinations (Copy, Paste, Cut, Select All, Undo, Redo)
    if (event.ctrlKey || event.metaKey) {
      const allowedCombinations = ['a', 'c', 'v', 'x', 'z', 'y'];
      if (allowedCombinations.includes(key.toLowerCase())) {
        return;
      }
    }

    // Block 'e' and 'E' (scientific notation)
    if (key.toLowerCase() === 'e') {
      event.preventDefault();
      return;
    }

    // Block all non-numeric characters except decimal point
    if (!/[\d.]/.test(key)) {
      event.preventDefault();
      return;
    }

    // Allow only one decimal point
    if (key === '.' && currentValue.includes('.')) {
      event.preventDefault();
      return;
    }

    // Prevent multiple leading zeros
    if (key === '0' && currentValue === '0') {
      event.preventDefault();
      return;
    }
  }

  /**
   * Filter monthly income data to stop at retirement date
   */
  getFilteredMonthlyIncomeData(): any[] {
    if (!this.employmentInfo.dateOfRetirement) {
      return this.monthlyIncomeData;
    }

    const retirementDate = new Date(this.employmentInfo.dateOfRetirement);
    const retirementYear = retirementDate.getFullYear();
    const retirementMonth = retirementDate.getMonth(); // 0-based (0=Jan, 1=Feb, etc.)

    return this.monthlyIncomeData.filter(row => {
      if (row.year < retirementYear) {
        return true; // Show all months before retirement year
      } else if (row.year === retirementYear) {
        // For retirement year, only show months up to and including retirement month
        const rowMonthIndex = MONTH_NAMES.indexOf(row.month);
        return rowMonthIndex <= retirementMonth;
      } else {
        return false; // Don't show months after retirement year
      }
    });
  }

  /**
   * Clear all form data to prevent showing stale values
   */
  private clearFormData(): void {
    this.employmentInfo = {
      cafo: "",
      dateOfBirth: "",
      hireDate: "",
      dateOfRetirement: "",
      dateOfDisability: this.getCurrentDateFormatted(),
    };

    this.performanceBenefits = {
      longevity: 0,
      rate: "",
      tfps: 0,
      averageTfp: 0,
      mbe: 0,
      sickBankStart: 0,
      sickUseAfterEp: "1.0",
    };

    this.financialInfo = {
      federalTaxBracket: "",
      stateTaxBracket: "",
      preTax: 0,
      roth: 0,
      weeksOfVacation: 0,
      stdPlan: "",
      ltdPlan: "",
      lolTaxable: "",
    };
  }
}
